// ActionableNotificationModal — Fase 3 (Feb 2026).
// Opens ON THE CURRENT PAGE when a user clicks an actionable notification.
// Does NOT navigate away. Approve/reject are only shown when the caller is
// server-side authorised (owner/manager/teamlead) — the actual permission is
// re-checked on the API call, so a spoofed frontend button is harmless.
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { X, Check, XCircle, Calendar, Repeat2, Clock, User as UserIcon } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { toast } from "sonner";

const KIND_META = {
  leave: { icon: Calendar, title: "Verlofaanvraag",   titleEN: "Leave request" },
  swap:  { icon: Repeat2,  title: "Dienstwissel",     titleEN: "Shift swap" },
  time:  { icon: Clock,    title: "Uren-goedkeuring", titleEN: "Time approval" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("nl", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}
function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

/**
 * @param {object} props
 * @param {"leave"|"swap"|"time"|null} props.kind
 * @param {object|null} props.data     the full request object from GET
 * @param {()=>void} props.onClose
 * @param {(next:object)=>void} [props.onDecided]  called after approve/reject
 */
export default function ActionableNotificationModal({ kind, data, onClose, onDecided }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!kind || !data) return null;
  const meta = KIND_META[kind] || KIND_META.leave;
  const Icon = meta.icon;
  const isMgmt = ["owner", "manager", "teamlead"].includes(user?.role);
  const isPending = (data.status || "").toLowerCase() === "pending"
    || (kind === "swap" && ["accepted", "pending"].includes(data.status));

  const doAction = async (action) => {
    setBusy(true);
    try {
      let r;
      if (kind === "leave") {
        r = await api.post(`/leave-requests/${data._id}/decide`,
          { status: action === "approve" ? "approved" : "rejected" });
      } else if (kind === "swap") {
        r = await api.post(`/swap-requests/${data._id}/action`,
          { action: action === "approve" ? "approve" : "decline" });
      } else if (kind === "time") {
        r = await api.post(`/time-approvals/${data._id}/decide`,
          { status: action === "approve" ? "approved" : "rejected" });
      }
      toast.success(action === "approve" ? "Goedgekeurd" : "Afgewezen");
      onDecided?.(r?.data);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Actie mislukt";
      toast.error(typeof msg === "string" ? msg : "Actie mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="ov"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
        data-testid="actionable-modal"
      >
        <motion.div
          key="pn"
          initial={{ scale: 0.94, opacity: 0, y: 12 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.94, opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="card w-full max-w-md p-0 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          style={{ boxShadow: "var(--shadow-xl)" }}
        >
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-9 h-9 rounded-xl grid place-items-center bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] shrink-0">
                <Icon size={16} strokeWidth={2.2} />
              </span>
              <div className="min-w-0">
                <div className="eyebrow">Actie vereist</div>
                <div className="font-display text-[17px] font-bold text-[hsl(var(--text-strong))] leading-tight truncate">
                  {meta.title}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label={t("close")}
              data-testid="actionable-modal-close"
              className="w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))]"
            ><X size={16} /></button>
          </div>

          <div className="px-5 pb-4 space-y-2.5 text-[13px]">
            {kind === "leave" && <>
              <Row icon={UserIcon} label="Medewerker" value={data.employee_name} />
              <Row label="Periode" value={`${fmtDate(data.start_date)} — ${fmtDate(data.end_date)}`} />
              <Row label="Type"    value={data.leave_type} />
              <Row label="Status"  value={<StatusPill status={data.status} />} />
              {data.manager_note && <Row label="Notitie" value={data.manager_note} />}
            </>}
            {kind === "swap" && <>
              <Row icon={UserIcon} label="Aanvrager" value={data.from_employee_name} />
              <Row label="Doel"     value={data.target_employee_name || "Open"} />
              <Row label="Dienst"   value={data.shift_summary} />
              <Row label="Status"   value={<StatusPill status={data.status} />} />
              {data.accepted_by_name && <Row label="Geaccepteerd door" value={`${data.accepted_by_name} · ${fmtDT(data.accepted_at)}`} />}
              {data.approved_by_name && <Row label="Goedgekeurd door" value={`${data.approved_by_name} · ${fmtDT(data.approved_at)}`} />}
            </>}
            {kind === "time" && <>
              <Row icon={UserIcon} label="Medewerker" value={data.employee_name} />
              <Row label="Datum"        value={fmtDate(data.shift_date)} />
              <Row label="Ingediend"    value={`${data.actual_start || "—"} — ${data.actual_end || "—"}`} />
              <Row label="Status"       value={<StatusPill status={data.status} />} />
            </>}
          </div>

          <div className="flex items-center justify-between gap-2 px-5 py-4 border-t hairline bg-[hsl(var(--bg))]">
            <button
              onClick={onClose}
              className="btn btn-quiet"
              data-testid="actionable-modal-cancel"
            >Sluiten</button>
            {isMgmt && isPending && (
              <div className="flex gap-2">
                <button
                  onClick={() => doAction("reject")}
                  disabled={busy}
                  data-testid="actionable-reject"
                  className="btn btn-quiet !text-[hsl(var(--danger))]"
                ><XCircle size={14} strokeWidth={2} /> Afwijzen</button>
                <button
                  onClick={() => doAction("approve")}
                  disabled={busy}
                  data-testid="actionable-approve"
                  className="btn btn-primary"
                ><Check size={14} strokeWidth={2} /> Goedkeuren</button>
              </div>
            )}
            {(!isMgmt || !isPending) && (
              <span className="text-[11px] text-[hsl(var(--text-muted))]" data-testid="actionable-readonly">
                Alleen-lezen
              </span>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Row({ icon: Ic, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-24 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--text-muted))] pt-0.5 flex items-center gap-1.5">
        {Ic && <Ic size={11} strokeWidth={2} />} {label}
      </div>
      <div className="flex-1 min-w-0 text-[13px] font-medium text-[hsl(var(--text-strong))] break-words">
        {value ?? "—"}
      </div>
    </div>
  );
}
function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const map = {
    pending:  "bg-[hsl(38_92%_96%)] text-[hsl(28_92%_42%)]",
    approved: "bg-[hsl(var(--success-soft))] text-[hsl(var(--success))]",
    accepted: "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]",
    rejected: "bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))]",
    declined: "bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))]",
    cancelled:"bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${map[s] || "bg-[hsl(var(--muted))]"}`}>
      {s || "—"}
    </span>
  );
}
